// GENERATED FILE — DO NOT EDIT.
//
// Copied verbatim from functions/src/services/whatsapp/service-billing.ts by functions/src/scripts/gen-media-spec.ts.
// Edit the source, then run: npm --prefix functions run gen:shared
// The functions predeploy hook fails if this file has drifted.
/**
 * Billing facts shared by the backend and the Angular app.
 *
 * This module is copied verbatim into `src/app/core/whatsapp/` by
 * `gen-media-spec.ts` and the copy is checked on predeploy, so it must have
 * **zero imports** and use no Node or DOM globals. `Date.UTC`, `Date.now` and
 * `toISOString` are ECMAScript and therefore fine.
 *
 * ## The one rule that must not be broken
 *
 * **The usage counter never consults `isServiceBillable()`.** It counts
 * whatever Meta marks `pricing.billable === true` on the status webhook. If
 * Meta ships early, ships late, or exempts a cohort, the counter is still
 * right. This predicate governs only forward-looking copy and pre-send
 * projection — the things that must read correctly *before* the first billed
 * message exists. Gating the counter on a date would reintroduce exactly the
 * "somebody has to deploy at midnight UTC" failure this constant exists to
 * avoid.
 */

/**
 * When Meta begins charging for service (free-form) messages sent inside the
 * 24-hour customer service window.
 *
 * Service messages have been free since 1 November 2024. Utility templates
 * delivered inside an open window lose their free treatment on the same date.
 */
export const SERVICE_BILLING_START_MS = Date.UTC(2026, 9, 1); // 2026-10-01T00:00:00Z

/** The same instant as an ISO string, for copy that needs to print the date. */
export const SERVICE_BILLING_START_ISO = "2026-10-01T00:00:00.000Z";

/**
 * Whether service messages are billable at a given instant.
 * @param {number} [atMs] - Instant to test. Defaults to now.
 * @return {boolean} True from 1 October 2026 onwards.
 */
export function isServiceBillable(atMs: number = Date.now()): boolean {
    return atMs >= SERVICE_BILLING_START_MS;
}

/**
 * Whether a utility template delivered inside an open customer service window
 * is billable. Same cutover as service messages, named separately because they
 * are different line items on the invoice and could yet diverge.
 * @param {number} [atMs] - Instant to test. Defaults to now.
 * @return {boolean} True from 1 October 2026 onwards.
 */
export function isInWindowUtilityBillable(atMs: number = Date.now()): boolean {
    return atMs >= SERVICE_BILLING_START_MS;
}

/**
 * The billing period a message falls in, as `YYYY-MM`.
 *
 * **UTC, deliberately.** Meta's WABA invoice period is a UTC calendar month;
 * bucketing by anything else makes reconciliation against the invoice
 * impossible. The pre-existing usage code builds months in ambient server time,
 * which is correct in production only because Cloud Run defaults to UTC and is
 * two hours wrong in a local emulator on a South African machine.
 *
 * @param {number} [atMs] - Instant to bucket. Defaults to now.
 * @return {string} e.g. "2026-10".
 */
export function billingPeriodKey(atMs: number = Date.now()): string {
    return new Date(atMs).toISOString().slice(0, 7);
}

/**
 * Half-open bounds of a billing period: `startMs <= t < endMs`.
 * @param {string} period - A key from `billingPeriodKey`, e.g. "2026-10".
 * @return {{startMs: number, endMs: number}} UTC bounds, end exclusive.
 * @throws {Error} When the period is not `YYYY-MM`.
 */
export function billingPeriodBounds(period: string): { startMs: number; endMs: number } {
    const match = /^(\d{4})-(\d{2})$/.exec(period);
    if (!match) {
        throw new Error(`Invalid billing period "${period}" — expected YYYY-MM.`);
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) {
        throw new Error(`Invalid billing period "${period}" — month out of range.`);
    }
    // Date.UTC rolls month 12 into January of the next year on its own.
    return {
        startMs: Date.UTC(year, month - 1, 1),
        endMs: Date.UTC(year, month, 1)
    };
}

/** The four categories Echo meters, caps and prices. */
export const BILLING_CATEGORIES = ["marketing", "utility", "authentication", "service"] as const;

export type BillingCategory = typeof BILLING_CATEGORIES[number];

/** What `normalizeBillingCategory` can return: a real category, or a bucket for what we cannot price. */
export type NormalizedCategory = BillingCategory | "referral_conversion" | "unknown";

/**
 * Map Meta's `pricing.category` onto one of our buckets.
 *
 * Meta sends values we do not price one-for-one:
 *  - `authentication_international` is a different *rate* but the same
 *    *limit bucket* as authentication. The raw value is kept separately on the
 *    counter's `types` map so spend can still be reconciled per rate.
 *  - `referral_conversion` is free and gets its own bucket.
 *
 * Anything unrecognised becomes `"unknown"` and is **never** folded into
 * `utility`. The existing code defaults unknown categories to utility, which
 * silently attributes them to the utility spend cap.
 *
 * @param {string} [raw] - Meta's category string, any casing.
 * @return {NormalizedCategory} The bucket to count against.
 */
export function normalizeBillingCategory(raw?: string): NormalizedCategory {
    const value = (raw || "").trim().toLowerCase();
    switch (value) {
    case "marketing":
        return "marketing";
    case "utility":
        return "utility";
    case "authentication":
    case "authentication_international":
    case "authentication-international":
        return "authentication";
    case "service":
        return "service";
    case "referral_conversion":
    case "referral-conversion":
        return "referral_conversion";
    default:
        return "unknown";
    }
}

/**
 * Whether a normalized category is one Echo prices and caps.
 * @param {NormalizedCategory} category - The bucket to test.
 * @return {boolean} True for the four billable categories.
 */
export function isBillingCategory(category: NormalizedCategory): category is BillingCategory {
    return (BILLING_CATEGORIES as readonly string[]).indexOf(category) !== -1;
}

/*
 * ---------------------------------------------------------------------------
 * Market resolution
 * ---------------------------------------------------------------------------
 *
 * Meta prices by **market**, not by country. Roughly forty markets are single
 * countries and carry their own rate row; every other country resolves through
 * one of eight regional groupings.
 *
 * The pricing page currently matches a rate row on `code === org.countryCode`
 * and, finding nothing for a country that sits in a regional grouping, falls
 * back to `rates[0]` — an unrelated country's rate, with no error state. This
 * map is what replaces that guess.
 *
 * PROVENANCE — READ BEFORE TRUSTING THIS TABLE.
 *
 * The authoritative source is the rate card CSV linked from
 * https://developers.facebook.com/documentation/business-messaging/whatsapp/pricing
 * The membership below was assembled from Meta's published market groupings for
 * the rate card noted in MARKET_MAP_RATE_CARD. It has NOT been diffed against
 * the CSV row by row. Before seeding production pricing, download the CSV and
 * reconcile: a country in the wrong grouping shows a customer a wrong price,
 * and the failure is silent.
 *
 * Meta also moves countries out of regional groupings into standalone rows over
 * time (Bangladesh, Iraq, Nepal, Sri Lanka, Kazakhstan, Kuwait, Morocco, Oman
 * and Ukraine all became standalone during 2026). Re-reconcile whenever the
 * rate card version changes.
 *
 * NOTE: Meta bills on the **recipient's** country, not the business's. This map
 * resolves an organisation's own market for display and forecasting, which is a
 * good approximation for an indicator and is not an invoice. Say so in the UI.
 */

/** The rate card this market map was assembled against. */
export const MARKET_MAP_RATE_CARD = "2026-10-01";

/** Regional groupings. Standalone markets use their ISO 3166-1 alpha-2 code as the market code. */
export const REGION_MARKETS = [
    "NORTH_AMERICA",
    "REST_OF_AFRICA",
    "REST_OF_ASIA_PACIFIC",
    "REST_OF_CEE",
    "REST_OF_LATAM",
    "REST_OF_MIDDLE_EAST",
    "REST_OF_WESTERN_EUROPE",
    "OTHER"
] as const;

/** Market a country resolves to when we have no mapping for it. */
export const DEFAULT_MARKET = "OTHER";

/**
 * Countries that carry their own rate row. The market code is the ISO code.
 * Kept as an explicit list so `marketForCountry` maps them to themselves
 * without a special case, and so the seed script can assert every one of these
 * has a row in the rate card.
 */
export const STANDALONE_MARKETS: readonly string[] = [
    "AE", "AR", "BD", "BR", "CL", "CO", "DE", "EG", "ES", "FR",
    "GB", "HK", "HU", "ID", "IL", "IN", "IQ", "IT", "KW", "KZ",
    "LK", "MA", "MX", "MY", "NG", "NL", "NP", "OM", "PE", "PK",
    "PL", "QA", "RO", "RU", "SA", "SG", "TR", "UA", "ZA"
];

/** Countries that resolve through a regional grouping. */
const REGION_MEMBERS: Readonly<Record<string, readonly string[]>> = {
    NORTH_AMERICA: ["CA", "US"],
    REST_OF_AFRICA: [
        "AO", "BF", "BI", "BJ", "BW", "CD", "CG", "CI", "CM", "DZ",
        "ER", "ET", "GA", "GH", "GM", "GN", "GW", "KE", "LR", "LS",
        "LY", "MG", "ML", "MR", "MU", "MW", "MZ", "NA", "NE", "RW",
        "SD", "SL", "SN", "SO", "SS", "SZ", "TD", "TG", "TN", "TZ",
        "UG", "ZM", "ZW"
    ],
    REST_OF_ASIA_PACIFIC: [
        "AF", "AU", "BN", "BT", "CN", "FJ", "JP", "KH", "KR", "LA",
        "MM", "MN", "MV", "NZ", "PG", "PH", "TH", "TW", "VN"
    ],
    REST_OF_CEE: [
        "AL", "AM", "AZ", "BA", "BG", "BY", "CZ", "EE", "GE", "GR",
        "HR", "LT", "LV", "MD", "ME", "MK", "RS", "SI", "SK"
    ],
    REST_OF_LATAM: [
        "BO", "CR", "CU", "DO", "EC", "GT", "HN", "JM", "NI", "PA",
        "PY", "SV", "TT", "UY", "VE"
    ],
    REST_OF_MIDDLE_EAST: ["BH", "JO", "LB", "PS", "SY", "YE"],
    REST_OF_WESTERN_EUROPE: ["AT", "BE", "CH", "DK", "FI", "IE", "IS", "LU", "NO", "PT", "SE"]
};

/**
 * ISO alpha-2 country code to market code.
 *
 * Built once at module load from `STANDALONE_MARKETS` (identity) and
 * `REGION_MEMBERS`, so the two lists cannot drift from the lookup.
 */
export const COUNTRY_TO_MARKET: Readonly<Record<string, string>> = (() => {
    const map: Record<string, string> = {};
    for (const iso of STANDALONE_MARKETS) {
        map[iso] = iso;
    }
    for (const region of Object.keys(REGION_MEMBERS)) {
        for (const iso of REGION_MEMBERS[region]) {
            map[iso] = region;
        }
    }
    return map;
})();

/**
 * The market an organisation's country prices against.
 * @param {string} [iso] - ISO 3166-1 alpha-2 country code, any casing.
 * @return {string} A market code; `OTHER` when the country is unmapped.
 */
export function marketForCountry(iso?: string): string {
    if (!iso) return DEFAULT_MARKET;
    return COUNTRY_TO_MARKET[iso.trim().toUpperCase()] || DEFAULT_MARKET;
}

/*
 * ---------------------------------------------------------------------------
 * Dated revisions
 * ---------------------------------------------------------------------------
 *
 * Rate cards arrive with an effective date, and two are live at once across a
 * cutover. Both the backend and the browser have to agree on which one applies
 * at a given instant, so the selection lives here rather than in either.
 *
 * Generic over anything carrying `effectiveFromMs` so this module keeps its zero
 * imports and the rate card types can stay where they belong.
 */

/** Anything that takes effect at a known instant. */
export interface Dated {
    effectiveFromMs: number;
}

/**
 * The revision in force at an instant.
 * @param {T[]} revisions - Candidates, in any order.
 * @param {number} [atMs] - Instant to resolve at. Defaults to now.
 * @return {T} The latest revision already in effect; the earliest known one when
 *     the instant predates them all; null for an empty list.
 */
export function pickEffective<T extends Dated>(revisions: T[], atMs: number = Date.now()): T | null {
    if (!revisions || !revisions.length) return null;

    const ordered = revisions.slice().sort((a, b) => a.effectiveFromMs - b.effectiveFromMs);
    let current: T | null = null;
    for (const revision of ordered) {
        if (revision.effectiveFromMs <= atMs) current = revision;
    }
    // Before the first revision the oldest card is the best available answer.
    return current || ordered[0];
}

/**
 * The next revision that has not taken effect yet.
 * @param {T[]} revisions - Candidates, in any order.
 * @param {number} [atMs] - Instant to resolve at. Defaults to now.
 * @return {T} The next future revision, or null when the newest is already live.
 */
export function pickUpcoming<T extends Dated>(revisions: T[], atMs: number = Date.now()): T | null {
    const future = (revisions || [])
        .filter((r) => r.effectiveFromMs > atMs)
        .sort((a, b) => a.effectiveFromMs - b.effectiveFromMs);
    return future.length ? future[0] : null;
}
